/**
 * Map API listing from /private-listings/paginated (or /recommendations/listings) to BuySignalCard signal shape.
 * Supports both shapes: vehicle_make/model/year/trim, asking_price, images, url, listing_date.
 */

function gradeToBuyScore(grade) {
  const gradeMap = {
    "A+": 95, "A": 90, "A-": 85,
    "B+": 82, "B": 78, "B-": 75,
    "C+": 72, "C": 68, "C-": 65
  };
  return gradeMap[grade] || 70;
}

/** Derive listing site from URL or API field for filtering (Facebook, OfferUp, Craigslist). */
function getListingSite(listing) {
  const site = listing.listing_site ?? listing.listingSite ?? listing.source;
  if (site) {
    const s = String(site).toLowerCase();
    if (s.includes("facebook")) return "Facebook";
    if (s.includes("offerup")) return "OfferUp";
    if (s.includes("craigslist")) return "Craigslist";
  }
  const url = listing.url ?? listing.vdp_url ?? "";
  if (url && typeof url === "string") {
    if (url.includes("facebook.com")) return "Facebook";
    if (url.includes("offerup.com")) return "OfferUp";
    if (url.includes("craigslist.org")) return "Craigslist";
  }
  return null;
}

export function mapListingToSignal(listing) {
  if (!listing || typeof listing !== "object") return null;
  const activeCount = listing.active_count ?? listing.units_active ?? 0;
  const soldCount = listing.units_sold ?? listing.sold_count ?? 0;
  // Prefer computing average DOM directly from sold comps when available
  const soldListings = listing.sold_listings || [];
  let soldDom = listing.sold_dom ?? listing.dom ?? 0;
  if (Array.isArray(soldListings) && soldListings.length > 0) {
    const domValues = soldListings
      .map((sale) => sale.dom ?? sale.dom_active ?? 0)
      .filter((dom) => typeof dom === "number" && dom > 0);
    if (domValues.length > 0) {
      const sum = domValues.reduce((acc, dom) => acc + dom, 0);
      soldDom = Math.round(sum / domValues.length);
    }
  }
  const city = listing.city || "";
  const locationStr = listing.location ?? (city ? `${city}, CA` : null);
  const price = listing.asking_price ?? listing.price ?? 0;
  const retail = listing.retail_price ?? listing.retail ?? listing.msrp ?? listing.asking_price ?? listing.price ?? 0;
  const mileage = listing.miles ?? listing.mileage ?? 0;
  const images = listing.images ?? listing.photos ?? [];
  const thumbnailUrl = Array.isArray(images) && images.length > 0
    ? images[0]
    : listing.photo || null;
  return {
    id: listing.id ?? listing.vin ?? Math.random(),
    recommended: listing.recommended === true || listing.recommended === "true",
    underpriced: listing.underpriced === true || listing.underpriced === "true",
    year: listing.vehicle_year ?? listing.year,
    make: listing.vehicle_make ?? listing.make,
    model: listing.vehicle_model ?? listing.model,
    trim: listing.vehicle_trim ?? listing.trim,
    price: typeof price === "number" ? price : Number(price) || 0,
    mileage: typeof mileage === "number" ? mileage : Number(mileage) || 0,
    sellerType: "Private",
    location: locationStr || "—",
    distanceMi: listing.distance_miles ?? listing.distance_mi ?? listing.distance ?? null,
    dom: null,
    buyScore: gradeToBuyScore(listing.grade),
    profitPotential: listing.profit,
    tags: listing.grade === "A+" ? ["Fast Mover"] : listing.grade === "A" ? ["High Confidence"] : ["Caution"],
    titleStatus: "Clean",
    activeCount,
    soldCount,
    soldDom,
    listedAt: listing.listing_date ?? listing.fetched_at,
    thumbnailUrl,
    photos: images,
    vdpUrl: listing.url ?? listing.vdp_url,
    retail: typeof retail === "number" ? retail : Number(retail) || 0,
    msrp: listing.msrp ?? (typeof retail === "number" ? retail : Number(retail) || 0),
    version: listing.vehicle_version ?? listing.version ?? null,
    active_listings: listing.active_listings || [],
    sold_listings: listing.sold_listings || [],
    listingSite: getListingSite(listing),
    viewedAt: listing.viewed_at ?? null,
  };
}
